import Phaser from 'phaser';
import { CHAPTERS } from '../config/levels';

/**
 * The tab row across the top of the episode menu: CHAPTER 1-3 and LEADERBOARDS.
 *
 * It lives here rather than inside LevelSelectScene because two scenes now draw
 * it — the episode list and the leaderboard — and they must draw it IDENTICALLY
 * or switching tabs would visibly jog the row.
 *
 * Spec: docs/superpowers/specs/2026-09-20-leaderboards-design.md §1, §7
 */

const GOLD = 0xf5c518;
export const GOLD_HEX = '#f5c518';
/** Chapter 2 accent — ice blue, replacing gold on its episode cards/tab. */
const CH2_BLUE = 0x4aa3e0;
const CH2_BLUE_HEX = '#4aa3e0';
/** Chapter 3 accent — castle red-gold, for the Hambi Castle cards/tab. */
const CH3_RED = 0xe0603a;
const CH3_RED_HEX = '#e0603a';
/** Leaderboards accent — a green of its own, so the tab reads as a place. */
export const LEADERBOARD_GREEN_HEX = '#8ea87a';

/** Accent (fill number + text hex) for a chapter's cards and tab. */
export function chapterAccent(chapter: number): { num: number; hex: string } {
  if (chapter >= 3) return { num: CH3_RED, hex: CH3_RED_HEX };
  if (chapter === 2) return { num: CH2_BLUE, hex: CH2_BLUE_HEX };
  return { num: GOLD, hex: GOLD_HEX };
}

export const LEADERBOARD_TAB = 'leaderboards' as const;

/** Which tab is showing: a chapter number, or the leaderboard. */
export type TabId = number | typeof LEADERBOARD_TAB;

/** The row's y, shared by both scenes. */
export const TAB_ROW_Y = 108;

/**
 * Centre-to-centre spacing.
 *
 * Four pills have to share the line with the TEST button at x=1180, and they
 * cannot: at the old 300px pitch the row would reach x≈1215 and sit on top of
 * it. 240 puts the row in [188, 1092], keeping ~50px clear of TEST — the
 * headroom Linux CI's wider fonts need (see tests/e2e/overlap.spec.ts and the
 * DemoScene precedent).
 *
 * This is also why a locked chapter no longer spells out "· LOCKED": that label
 * alone measures ~260px, which no four-pill pitch can hold. A locked tab is
 * greyed, and tapping it still flashes exactly how to unlock it — which says
 * more than the word did.
 */
const PITCH = 240;

interface TabPill {
  id: TabId;
  txt: Phaser.GameObjects.Text;
}

export interface TabRowOptions {
  /** The tab currently showing. */
  selected: TabId;
  /** Is a chapter playable? (Chapter 1 always; others once their opener unlocks.) */
  isChapterUnlocked(chapter: number): boolean;
  /** A playable tab was tapped. */
  onSelect(id: TabId): void;
  /** A locked chapter was tapped — show the unlock hint. */
  onLocked(chapter: number): void;
  /** False in builds with no Supabase env: the fourth tab is not drawn at all. */
  showLeaderboards: boolean;
}

export interface TabRow {
  /** Re-colour after the selection changes. */
  restyle(selected: TabId): void;
  destroy(): void;
}

/** Build the centred row of tab pills. */
export function buildTabRow(scene: Phaser.Scene, options: TabRowOptions): TabRow {
  const ids: TabId[] = [...CHAPTERS];
  if (options.showLeaderboards) ids.push(LEADERBOARD_TAB);

  const pills: TabPill[] = [];
  let selected = options.selected;

  const totalW = (ids.length - 1) * PITCH;
  let x = 640 - totalW / 2;

  for (const id of ids) {
    const label = id === LEADERBOARD_TAB ? 'LEADERBOARDS' : `CHAPTER ${id}`;
    const txt = scene.add
      .text(x, TAB_ROW_Y, label, {
        fontSize: '16px',
        color: '#9aa6ad',
        fontStyle: 'bold',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5)
      .setLetterSpacing(2)
      .setInteractive({ useHandCursor: true });
    txt.on('pointerup', () => {
      if (id === selected) return;
      if (typeof id === 'number' && !options.isChapterUnlocked(id)) {
        options.onLocked(id);
        return;
      }
      options.onSelect(id);
    });
    pills.push({ id, txt });
    x += PITCH;
  }

  const restyle = (next: TabId): void => {
    selected = next;
    for (const { id, txt } of pills) {
      const isSelected = id === selected;
      if (id === LEADERBOARD_TAB) {
        txt.setColor(isSelected ? LEADERBOARD_GREEN_HEX : '#cfd3dc');
      } else {
        const unlocked = options.isChapterUnlocked(id);
        txt.setColor(isSelected ? chapterAccent(id).hex : unlocked ? '#cfd3dc' : '#6a7078');
      }
      txt.setBackgroundColor(isSelected ? '#262234' : '#15131f');
    }
  };
  restyle(selected);

  return {
    restyle,
    destroy: () => {
      for (const { txt } of pills) txt.destroy();
      pills.length = 0;
    },
  };
}
